import React from 'react';
import { Search, Filter, Grid3X3, List, Calendar } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const FilterBar = ({
  searchTerm,
  setSearchTerm,
  filterStatus,
  setFilterStatus,
  filterRole,
  setFilterRole,
  filterDateRange,
  setFilterDateRange,
  viewMode,
  setViewMode,
  activeTab
}) => {
  const { userProfile } = useAuth();

  const getStatusOptions = () => {
    switch (activeTab) {
      case 'reports':
        return [
          { value: 'draft', label: 'Draft' },
          { value: 'submitted', label: 'Submitted' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'revision_requested', label: 'Revision Requested' },
          { value: 'resolved', label: 'Resolved' },
          { value: 'dismissed', label: 'Dismissed' },
          { value: 'under_review', label: 'Under Review' }
        ];
      case 'users':
        return [
          { value: 'active', label: 'Active' },
          { value: 'suspended', label: 'Suspended' },
          { value: 'warned', label: 'Warned' },
          { value: 'banned', label: 'Banned' },
          { value: 'pending_verification', label: 'Verification Pending' }
        ];
      case 'jobs':
        return [
          { value: 'active', label: 'Active' },
          { value: 'draft', label: 'Draft' },
          { value: 'published', label: 'Published' },
          { value: 'paused', label: 'Paused' },
          { value: 'expired', label: 'Expired' },
          { value: 'filled', label: 'Filled' },
          { value: 'archived', label: 'Archived' },
          { value: 'flagged', label: 'Flagged' }
        ];
      default:
        return [];
    }
  };

  const getRoleOptions = () => {
    switch (activeTab) {
      case 'users': {
        const baseRoleOptions = [
          { value: 'va', label: 'Virtual Assistant' },
          { value: 'employer', label: 'Employer' },
          { value: 'admin', label: 'Admin' },
          { value: 'moderator', label: 'Moderator' }
        ];
        // Hide admin option if current user is a moderator
        if (userProfile?.role === 'moderator') {
          return baseRoleOptions.filter(option => option.value !== 'admin');
        }
        return baseRoleOptions;
      }
      case 'reports':
        return [
          { value: 'high', label: 'High Priority' },
          { value: 'medium', label: 'Medium Priority' },
          { value: 'low', label: 'Low Priority' }
        ];
      case 'jobs':
        return [
          { value: 'full-time', label: 'Full Time' },
          { value: 'part-time', label: 'Part Time' },
          { value: 'contract', label: 'Contract' },
          { value: 'freelance', label: 'Freelance' }
        ];
      default:
        return [];
    }
  };

  const getRoleLabel = () => {
    switch (activeTab) {
      case 'users':
        return 'All Roles';
      case 'reports':
        return 'All Priority';
      case 'jobs':
        return 'All Types';
      default:
        return 'All';
    }
  };

  const showViewToggle = ['users', 'jobs', 'reports'].includes(activeTab);
  const showAdditionalFilters = ['users', 'jobs', 'reports'].includes(activeTab);

  return (
    <div className="flex flex-col space-y-4 mb-6">
      {/* Main search and view toggle */}
      <div className="flex flex-row gap-4 ">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search users, reports, or jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className=" w-full md:w-1/2 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        {/* View Mode Toggle */}
        {showViewToggle && (
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="all">All Status</option>
            {getStatusOptions().map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Additional filters */}
        {showAdditionalFilters && (
          <>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">{getRoleLabel()}</option>
              {getRoleOptions().map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <select
                value={filterDateRange}
                onChange={(e) => setFilterDateRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
